/**
 * Submodel Lock Validator — Pure Rule Engine
 *
 * Implements the container↔submodel locking rules. Pure functions only — no React,
 * no context. Callers supply a `maxOf(submodelId)` resolver (in the app this comes
 * from ModuleContext.getSubmodelInstanceLimits, sourced from the Data Model spec).
 *
 * Rules implemented (see product spec / locking-rules visual guide):
 *  1. Every container locks to ONE submodel, set by its first DIRECT field/category/
 *     connector item. Only same-submodel items may be added after. (A container with
 *     no direct submodel-bound items is unlocked.)
 *  2. A container's lock does NOT cascade to nested children when its submodel is
 *     max=1 — each container is evaluated by its OWN direct items.
 *  3. Splitting one submodel across sibling containers is allowed (falls out of
 *     per-container independence — nothing here blocks it).
 *  4. A submodel with max instance > 1 locks its ENTIRE subtree to that one submodel,
 *     and must be alone (no other submodel anywhere in its container subtree).
 *  5. Field/category/connector global uniqueness is enforced elsewhere
 *     (UIModelStateContext usedFieldsMap/usedCategoriesMap/usedConnectorsMap) — NOT here.
 *  6. A child's submodel max must be >= its ancestor's bound submodel max
 *     (deeper containers can't support fewer instances).
 *
 * Note on "submodel-bound item": fields, categories AND connectors carry a `parentId`
 * pointing at their submodel, so all three determine/respect a container's lock.
 * Widgets have no parentId and never lock a container.
 */

import type { ContainerAPI, ContainerItemAPI } from '../../types/api.types';
import { isFieldAPI, isCategoryAPI, isConnectorAPI } from '../../types/api.types';
import {
  findContainerById,
  getContainerPath,
  getDescendants,
} from '../utils/containerTraversal';

// ============================================================================
// Types
// ============================================================================

export type LockDenyReason =
  | 'container-not-found'
  | 'container-locked' // Rule 1/2: this container is already locked to a different submodel
  | 'tree-locked-multi' // Rule 4: an ancestor's max>1 submodel locks this subtree
  | 'max-must-be-alone' // Rule 4: incoming max>1 submodel can't join a subtree using another submodel
  | 'max-propagation'; // Rule 6: incoming max < an ancestor's submodel max

export interface LockCheckResult {
  /** Whether the submodel-bound item may be added to the target container. */
  allowed: boolean;
  /** Why it was denied (omitted when allowed). */
  reason?: LockDenyReason;
  /** Human-readable explanation for tooltips / toasts (omitted when allowed). */
  message?: string;
  /** The submodel id responsible for the block (for richer UI messaging). */
  blockedBySubmodelId?: string;
}

/** Resolves a submodel's max instance by id. Defaults to 1 for unknown ids. */
export type MaxOfResolver = (submodelId: string | null | undefined) => number;

const ALLOWED: LockCheckResult = { allowed: true };

const deny = (
  reason: LockDenyReason,
  message: string,
  blockedBySubmodelId?: string
): LockCheckResult => ({ allowed: false, reason, message, blockedBySubmodelId });

// ============================================================================
// Lock derivation (pure)
// ============================================================================

/** True for items that belong to a submodel (carry a parentId): field, category, connector. */
const isSubmodelBound = (item: ContainerItemAPI): boolean =>
  (isFieldAPI(item) || isCategoryAPI(item) || isConnectorAPI(item)) &&
  Boolean((item as { parentId?: string }).parentId);

/**
 * The submodel a container is DIRECTLY locked to, derived from its own direct
 * field/category/connector items. Returns null when the container has no direct
 * submodel-bound items (i.e. it is unlocked — Rule 1).
 *
 * Intentionally ignores `content.submodel` (which can be a propagated/cached
 * binding on a child that has no direct items) so the lock reflects real content.
 */
export const getContainerDirectLock = (container: ContainerAPI): string | null => {
  for (const item of container.content?.items || []) {
    if (isSubmodelBound(item)) {
      return (item as { parentId?: string }).parentId ?? null;
    }
  }
  return null;
};

/** Whether a container currently holds direct items locking it to a submodel. */
export const isContainerLocked = (container: ContainerAPI): boolean =>
  getContainerDirectLock(container) !== null;

/**
 * All distinct submodel ids directly locked across a container and every descendant.
 * Used to enforce "a max>1 submodel must be alone in its subtree" (Rule 4).
 */
export const collectSubtreeLocks = (container: ContainerAPI): Set<string> => {
  const locks = new Set<string>();
  const self = getContainerDirectLock(container);
  if (self) locks.add(self);
  for (const descendant of getDescendants(container)) {
    const lock = getContainerDirectLock(descendant);
    if (lock) locks.add(lock);
  }
  return locks;
};

// ============================================================================
// Main check
// ============================================================================

/**
 * Can a submodel-bound item (field/category/connector) belonging to `incomingSubmodelId`
 * be added to the container `targetContainerId`?
 *
 * @param containers - the canvas's top-level containers (the tree to evaluate within)
 * @param targetContainerId - the container the item is being added to
 * @param incomingSubmodelId - the submodel the item belongs to (its parentId)
 * @param maxOf - resolves a submodel's max instance by id
 */
export const canAddSubmodelItemToContainer = (
  containers: ContainerAPI[],
  targetContainerId: string,
  incomingSubmodelId: string,
  maxOf: MaxOfResolver
): LockCheckResult => {
  const target = findContainerById(containers, targetContainerId);
  if (!target) {
    return deny('container-not-found', 'Container not found.');
  }

  const incomingMax = Math.max(1, maxOf(incomingSubmodelId) || 1);

  // Rule 1 / Rule 2: the container's OWN lock (no cascade for max=1 — ancestors not consulted here).
  const directLock = getContainerDirectLock(target);
  if (directLock && directLock !== incomingSubmodelId) {
    return deny(
      'container-locked',
      'This container is already locked to another submodel. Remove its fields to use a different one.',
      directLock
    );
  }

  // Walk ancestors for the cascading rules (Rule 4) and max propagation (Rule 6).
  const path = getContainerPath(containers, targetContainerId); // [root, ..., target]
  const ancestorIds = path.slice(0, -1);
  for (const ancestorId of ancestorIds) {
    const ancestor = findContainerById(containers, ancestorId);
    if (!ancestor) continue;
    const ancestorLock = getContainerDirectLock(ancestor);
    if (!ancestorLock) continue;
    const ancestorMax = Math.max(1, maxOf(ancestorLock) || 1);

    // Rule 4: a max>1 ancestor locks the whole subtree to its submodel.
    if (ancestorMax > 1 && ancestorLock !== incomingSubmodelId) {
      return deny(
        'tree-locked-multi',
        `This group is locked to a multiple-instance submodel (max ${ancestorMax}). Only that submodel can be used anywhere inside it.`,
        ancestorLock
      );
    }

    // Rule 6: a deeper container can't have a smaller max than an ancestor.
    if (incomingMax < ancestorMax) {
      return deny(
        'max-propagation',
        `This submodel's max instance (${incomingMax}) is lower than an ancestor's (${ancestorMax}); a nested container can't support fewer instances than the one above it.`,
        ancestorLock
      );
    }
  }

  // Rule 4 (incoming side): a max>1 submodel must be alone — no other submodel
  // anywhere in the target's subtree (its descendants).
  if (incomingMax > 1) {
    const subtreeLocks = Array.from(collectSubtreeLocks(target));
    for (const lockId of subtreeLocks) {
      if (lockId !== incomingSubmodelId) {
        return deny(
          'max-must-be-alone',
          `A submodel with max instance ${incomingMax} must be alone — this container's group already uses another submodel.`,
          lockId
        );
      }
    }
  }

  return ALLOWED;
};

/**
 * Resolve whether a container is "tree-locked" to a multi-instance submodel by an
 * ancestor (or itself). Returns that submodel id, else null. Useful for UI badges
 * (e.g. "whole group locked to SM-X").
 */
export const getMultiInstanceTreeLock = (
  containers: ContainerAPI[],
  containerId: string,
  maxOf: MaxOfResolver
): string | null => {
  const path = getContainerPath(containers, containerId); // [root, ..., self]
  for (const id of path) {
    const c = findContainerById(containers, id);
    if (!c) continue;
    const lock = getContainerDirectLock(c);
    if (lock && Math.max(1, maxOf(lock) || 1) > 1) {
      return lock;
    }
  }
  return null;
};
